import { createModalFocusScope } from '../../components/modal-focus-scope.js';
import { readErrorMessage } from '../../components/write-request.js';

const DELETE_FALLBACK_MESSAGE = 'Failed to delete manual list.';
const DELETE_UNKNOWN_MESSAGE = 'Could not confirm the list was deleted. Reload the page before trying again.';
import { closeListBeforeBackdrop, comboboxKey, initGameFormCombobox } from '../game-form-combobox.js';

export function initManualListDeleteFlow(documentRef = globalThis.document, fetchImpl = globalThis.fetch) {
    const deleteForm = documentRef?.querySelector?.('[data-lists-manual-delete-form]');
    if (!(deleteForm instanceof HTMLFormElement)) {
        return;
    }

    const modal = documentRef?.querySelector?.('[data-lists-manual-delete-modal]');
    const dialog = modal instanceof HTMLElement
        ? modal.querySelector('[data-lists-manual-delete-dialog]')
        : null;
    const closeButtons = modal instanceof HTMLElement
        ? modal.querySelectorAll('[data-lists-manual-delete-close]')
        : null;
    const confirmButton = modal instanceof HTMLElement
        ? modal.querySelector('[data-lists-manual-delete-confirm]')
        : null;
    const errorElement = modal instanceof HTMLElement
        ? modal.querySelector('[data-lists-manual-delete-error]')
        : null;
    const impactElement = modal instanceof HTMLElement
        ? modal.querySelector('[data-lists-manual-delete-impact]')
        : null;
    const triggerButton = deleteForm.querySelector('[data-lists-manual-delete-trigger]');
    const backdrop = modal instanceof HTMLElement
        ? modal.querySelector('[data-lists-manual-delete-backdrop]')
        : null;

    if (
        !(modal instanceof HTMLElement)
        || !(dialog instanceof HTMLElement)
        || !(closeButtons instanceof NodeList)
        || !(confirmButton instanceof HTMLButtonElement)
        || typeof fetchImpl !== 'function'
    ) {
        return;
    }

    const defaultConfirmLabel = confirmButton.textContent?.trim() || 'Delete list';
    const defaultImpact = impactElement instanceof HTMLElement ? impactElement.textContent : '';
    let isDeleting = false;
    const idleConfirmLabel = () => ((moveTarget?.selectedTitle() ?? null) === null ? defaultConfirmLabel : 'Delete and move');
    const syncMoveTarget = () => {
        const targetTitle = moveTarget?.selectedTitle() ?? null;
        if (impactElement instanceof HTMLElement) {
            impactElement.textContent = targetTitle !== null
                ? `Games will be added to the end of "${targetTitle}"; games already there keep their place.`
                : defaultImpact;
        }
        if (!isDeleting) {
            confirmButton.textContent = idleConfirmLabel();
        }
    };
    const moveTarget = initMoveTarget(modal, syncMoveTarget);
    const backdropOnlyClosedList = moveTarget !== null && backdrop instanceof HTMLElement
        ? closeListBeforeBackdrop(backdrop, moveTarget.combobox)
        : () => false;

    const isObject = (value) => typeof value === 'object' && value !== null;

    const clearError = () => {
        if (!(errorElement instanceof HTMLElement)) {
            return;
        }

        errorElement.textContent = '';
        errorElement.hidden = true;
    };

    const setError = (message) => {
        if (!(errorElement instanceof HTMLElement)) {
            return;
        }

        errorElement.textContent = message;
        errorElement.hidden = false;
    };

    // A focused dialog button that gets disabled parks focus on the dialog, so it never leaves it.
    const setDeletingState = (active) => {
        isDeleting = active;
        const focused = documentRef?.activeElement ?? null;
        if (active && (focused === confirmButton || Array.from(closeButtons).includes(focused))) {
            dialog.focus();
        }
        confirmButton.disabled = active;
        confirmButton.textContent = active ? 'Deleting...' : idleConfirmLabel();

        closeButtons.forEach((button) => {
            if (button instanceof HTMLButtonElement) {
                button.disabled = active;
            }
        });

        if (triggerButton instanceof HTMLButtonElement) {
            triggerButton.disabled = active;
        }
    };

    const openModal = () => {
        if (isDeleting) {
            return;
        }

        clearError();
        modal.hidden = false;
        focusScope.activate(triggerButton);
        // Cancel, not the first focusable: the move-target combobox opens its options on focus.
        window.requestAnimationFrame(() => focusScope.focus(dialog.querySelector('[data-lists-manual-delete-close]')));
    };

    const closeModal = () => {
        if (modal.hidden || isDeleting) {
            return;
        }

        modal.hidden = true;
        clearError();
        // A closed dialog forgets its target, so the next delete starts with no move (as the dictionary
        // delete does); a failed request and a press that only closes the open list keep it.
        if (moveTarget !== null) {
            moveTarget.reset();
            syncMoveTarget();
        }
        focusScope.deactivate();
    };
    const focusScope = createModalFocusScope({
        dialog,
        requestClose: closeModal,
        documentRef,
    });
    if (focusScope === null) {
        return;
    }

    deleteForm.addEventListener('submit', (event) => {
        const requiresConfirmation = deleteForm.dataset.listsManualDeleteRequiresConfirmation === 'true';
        if (!requiresConfirmation) {
            return;
        }

        if (isDeleting) {
            event.preventDefault();
            return;
        }

        event.preventDefault();
        openModal();
    });

    closeButtons.forEach((button) => {
        button.addEventListener('click', () => {
            if (button === backdrop && backdropOnlyClosedList()) {
                return;
            }
            closeModal();
        });
    });

    confirmButton.addEventListener('click', async () => {
        if (isDeleting) {
            return;
        }

        clearError();
        setDeletingState(true);

        // A refusal shows the server's sentence or the fallback; no answer, or an unusable success, may have
        // deleted the list. The message travels as state, never through an exception.
        let message = DELETE_UNKNOWN_MESSAGE;
        try {
            const response = await fetchImpl(deleteForm.action, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                },
                credentials: 'same-origin',
                body: new FormData(deleteForm),
            });

            if (!response.ok) {
                message = await readErrorMessage(response, DELETE_FALLBACK_MESSAGE);
            } else {
                const payload = await response.json().catch(() => null);
                if (isObject(payload) && payload.success === true) {
                    const payloadRedirect = typeof payload.redirect === 'string'
                        ? payload.redirect.trim()
                        : '';
                    const fallbackRedirect = String(deleteForm.dataset.listsManualDeleteRedirectUrl ?? '').trim();
                    const redirectUrl = payloadRedirect !== ''
                        ? payloadRedirect
                        : (fallbackRedirect !== '' ? fallbackRedirect : '/lists');

                    window.location.assign(redirectUrl);

                    return;
                }
            }
        } catch (_error) {
            message = DELETE_UNKNOWN_MESSAGE;
        }

        setDeletingState(false);
        setError(message);
        // Focus returns to the confirm button unless it moved on inside the dialog.
        const focused = documentRef?.activeElement ?? null;
        if (focused === dialog || !dialog.contains(focused)) {
            confirmButton.focus();
        }
    });
}

/**
 * "Move games to": the native select (server-rendered, bound to the delete form with `form=`) is the
 * submitted value; the shared combobox only draws and searches over it, as in the dictionary delete.
 * Absent when the list has no games or there is no other manual list.
 */
function initMoveTarget(modal, onChange) {
    const field = modal.querySelector('[data-lists-manual-delete-target]');
    const select = modal.querySelector('[data-lists-manual-delete-target-select]');
    const comboboxElement = modal.querySelector('[data-lists-manual-delete-target-combobox]');
    const input = modal.querySelector('[data-lists-manual-delete-target-input]');
    const toggle = modal.querySelector('[data-lists-manual-delete-target-toggle]');
    const list = modal.querySelector('[data-lists-manual-delete-target-options]');
    if (
        !(field instanceof HTMLElement)
        || !(select instanceof HTMLSelectElement)
        || !(comboboxElement instanceof HTMLElement)
        || !(input instanceof HTMLInputElement)
        || !(toggle instanceof HTMLButtonElement)
        || !(list instanceof HTMLUListElement)
    ) {
        return null;
    }

    const options = Array.from(select.options).map((option) => ({
        type: 'option',
        value: option.value,
        label: option.textContent.trim(),
    }));
    const selectedTitle = () => (select.value === ''
        ? null
        : options.find((option) => option.value === select.value)?.label ?? null);
    // The empty choice shows as the placeholder, so the field reads "Don't move" until a pick.
    const showSelected = () => {
        input.value = selectedTitle() ?? '';
    };

    const combobox = initGameFormCombobox({
        root: field,
        input,
        toggle,
        list,
        buildOptions: (query) => {
            const queryKey = comboboxKey(query);
            const matches = options.filter((option) => comboboxKey(option.label).includes(queryKey));

            return matches.length > 0 ? matches : [{ type: 'empty', value: '', label: 'No matching lists.' }];
        },
        onSelect: (option) => {
            select.value = option.value;
            showSelected();
            onChange();
        },
        onClose: showSelected,
        isCurrent: (option) => option.value === select.value,
        filterOnly: true,
    });
    select.hidden = true;
    comboboxElement.hidden = false;

    return {
        selectedTitle,
        combobox,
        reset() {
            combobox.close();
            select.value = '';
            showSelected();
        },
    };
}
